<template>
  <div>
    <h1 class="section-title">Setup List Details</h1>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">New collaborative list name</label>
          <b-form-input
              type="text"
              :state="informationValidation.isCollaborativeListNameProvided"
              v-model="collaborativeListDetails.toDoListName"></b-form-input>
          <b-form-invalid-feedback v-if="!informationValidation.isCollaborativeListNameProvided">
            This field must not be empty.
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
    </div>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">List description (optional)</label>
          <b-form-input
              type="text"
              v-model="collaborativeListDetails.toDoListDescription"></b-form-input>
        </b-col>
      </b-row>
    </div>
    <hr>
    <h1 class="section-title">Invite List Collaborator</h1>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">Collaborator's email address</label>
          <b-form-input
              type="email"
              :state="informationValidation.isCollaboratorEmailProvided && informationValidation.isCollaboratorEmailValid && !isCollaboratorEmailAlreadyInvited && isCollaboratorEmailRegistered && isCurrentEmailProvided"
              v-model="collaboratorEmail"></b-form-input>

          <b-form-invalid-feedback v-if="!informationValidation.isCollaboratorEmailProvided">
            This field must not be empty.
          </b-form-invalid-feedback>

          <b-form-invalid-feedback v-else-if="!informationValidation.isCollaboratorEmailValid">
            Please provide a valid email address.
          </b-form-invalid-feedback>

          <b-form-invalid-feedback v-else-if="isCurrentEmailProvided">
            You are one of the list collaborators
          </b-form-invalid-feedback>

          <b-form-invalid-feedback v-else-if="isCollaboratorEmailAlreadyInvited">
            The collaborator has been included in the list
          </b-form-invalid-feedback>

          <b-form-invalid-feedback v-else-if="!isCollaboratorEmailRegistered">
            The email address provided is not registered in the system.
          </b-form-invalid-feedback>

          <b-form-text>Only registered email address will be invited to the list.</b-form-text>
        </b-col>
      </b-row>
    </div>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">Set collaborators's role</label>
          <b-form-select v-model="roleSelected" :options="roleOptions"></b-form-select>
        </b-col>
      </b-row>
    </div>
    <div class="input-container">
      <b-row>
        <b-col>
          <b-button class="secondary-input-button" block @click="inviteCollaborator">Invite collaborator</b-button>
        </b-col>
      </b-row>
    </div>
    <hr>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">Collaborators</label>
          <div class="collaborator-container">
            <invite-new-collaborator-list-cell v-for="(collaborator, index) in collaborativeListDetails.collaboratorListArray"
                                               :key="collaborator.id" :user-details="collaborator"
                                               :user-role="collaborativeListDetails.collaboratorRoleArray[index]"></invite-new-collaborator-list-cell>
            <p v-if="!informationValidation.isCollaboratorListValid && informationValidation.isCollaboratorListValid !== null "
               class="invalid-text">Please invite at least 1 collaborator to the list.</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <hr>
    <b-button class="main-input-button" block @click="createCollaborativeList">Create collaborative list</b-button>
  </div>
</template>

<script>
  import {email, minLength, required} from "vuelidate/lib/validators";
  import {db} from "../../../main";
  import InviteNewCollaboratorListCell from "../../table_cells/collaborator_cell/InviteNewCollaboratorListCell";
  import {mapGetters} from "vuex";

  export default {
    name: "CollaborativeListCreation.vue",
    components: {
      InviteNewCollaboratorListCell
    },
    data() {
      return {
        collaborativeListDetails: {
          collaboratorListArray: [],
          collaboratorRoleArray: [],
          toDoListCollaboratorId: [],
          toDoListDescription: null,
          toDoListName: null,
        },
        informationValidation: {
          isCollaborativeListNameProvided: null,
          isCollaboratorEmailProvided: null,
          isCollaboratorEmailValid: null,
          isCollaboratorListValid: null
        },
        collaboratorEmail: null,
        isCurrentEmailProvided: null,
        isCollaboratorEmailRegistered: null,
        isCollaboratorEmailAlreadyInvited: null,
        collaboratorEmailArray: [],
        roleSelected: 'Administrator',
        roleOptions: [
          {value: 'Administrator', text: 'Administrator'},
          {value: 'Member', text: 'Member'},
        ]
      }
    },
    validations: {
      collaborativeListDetails: {
        toDoListName: {
          required
        },
        collaboratorListArray: {
          required,
          minLength: minLength(2)
        }
      },
      collaboratorEmail: {
        required,
        email
      },
    },
    methods: {
      inviteCollaborator() {
        // check if all information is valid
        let isValid = true

        if (!this.$v.collaboratorEmail.required) {
          this.informationValidation.isCollaboratorEmailProvided = false
          isValid = false
        } else {
          this.informationValidation.isCollaboratorEmailProvided = true
        }

        if (!this.$v.collaboratorEmail.email) {
          this.informationValidation.isCollaboratorEmailValid = false
          isValid = false
        } else {
          this.informationValidation.isCollaboratorEmailValid = true
        }

        // check if the user provide their own email address
        if (this.collaboratorEmail === this.$store.getters.user.data.email) {
          this.isCurrentEmailProvided = true
          isValid = false
        } else {
          this.isCurrentEmailProvided = false
        }

        // check if the email address has been invited to the collaborative list
        if (this.collaboratorEmailArray.includes(this.collaboratorEmail)) {
          this.isCollaboratorEmailAlreadyInvited = true
          isValid = false
        }

        if (isValid) {
          this.isCollaboratorEmailAlreadyInvited = false

          db
            .collection('users/')
            .where('email', '==', this.collaboratorEmail)
            .get()
            .then(querySnapshot => {
              const userObject = querySnapshot.docs.map(doc => doc.data())
              // check whether the user is registered in the database
              if (userObject.length !== 0) {
                const collaboratorObject = userObject[0]

                this.collaborativeListDetails.collaboratorListArray.push(collaboratorObject)
                this.collaboratorEmailArray.push(collaboratorObject.email)
                this.collaborativeListDetails.collaboratorRoleArray.push(this.roleSelected)
                this.collaborativeListDetails.toDoListCollaboratorId.push(collaboratorObject.id)

                this.collaboratorEmail = null
                this.informationValidation.isCollaboratorEmailProvided = null
                this.informationValidation.isCollaboratorEmailValid = null
                this.isCollaboratorEmailRegistered = null
                this.isCollaboratorEmailAlreadyInvited = null
              } else {
                this.isCollaboratorEmailRegistered = false
              }
            })
        }
      },
      addPersonalDetails() {
        const personalDetailsObject = {
          id: this.$store.getters.user.data.id,
          name: this.$store.getters.user.data.displayName,
          email: this.$store.getters.user.data.email,
          profilePictureUrl: this.$store.getters.user.data.photoURL
        }

        this.collaborativeListDetails.collaboratorListArray.push(personalDetailsObject)
        this.collaboratorEmailArray.push(personalDetailsObject.email)
        this.collaborativeListDetails.collaboratorRoleArray.push('Administrator')
        this.collaborativeListDetails.toDoListCollaboratorId.push(personalDetailsObject.id)
      },
      createCollaborativeList() {
        // check if all information is valid
        let isValid = true
        this.informationValidation.isCollaboratorEmailProvided = null
        this.informationValidation.isCollaboratorEmailValid = null

        if (!this.$v.collaborativeListDetails.toDoListName.required) {
          this.informationValidation.isCollaborativeListNameProvided = false
          isValid = false
        } else {
          this.informationValidation.isCollaborativeListNameProvided = true
        }

        if (!this.$v.collaborativeListDetails.collaboratorListArray.required || !this.$v.collaborativeListDetails.collaboratorListArray.minLength) {
          this.informationValidation.isCollaboratorListValid = false
          isValid = false
        } else {
          this.informationValidation.isCollaboratorListValid = true
        }

        if (isValid) {
          this.$store.dispatch('createNewCollaborativeList', this.collaborativeListDetails)
          this.hideModal('creation-modal')
        }
      },
      hideModal(id) {
        this.$root.$emit('bv::hide::modal', id)
      }
    },
    computed: {
      ...mapGetters([
        "removedFromInvitationCollaboratorObject"
      ])
    },
    watch: {
      removedFromInvitationCollaboratorObject(newCollaboratorObject, oldCollaboratorObject) {
        if (newCollaboratorObject.id !== null) {
          const collaboratorIdToBeRemoved = newCollaboratorObject.id

          // find the index of the collaborator in the array to be removed
          const collaboratorIndex = this.collaborativeListDetails.toDoListCollaboratorId.indexOf(collaboratorIdToBeRemoved)

          if (collaboratorIndex > -1) {
            this.collaborativeListDetails.collaboratorListArray.splice(collaboratorIndex, 1)
            this.collaboratorEmailArray.splice(collaboratorIndex, 1)
            this.collaborativeListDetails.collaboratorRoleArray.splice(collaboratorIndex, 1)
            this.collaborativeListDetails.toDoListCollaboratorId.splice(collaboratorIndex, 1)
          }
        }
      },
    },
    mounted() {
      this.addPersonalDetails()
      this.$store.dispatch('setRemovedFromInvitationCollaboratorObject', null)
    },
    destroyed() {
      this.$store.dispatch('setRemovedFromInvitationCollaboratorObject', null)
    }
  }
</script>

<style scoped>
  .section-title {
    font-size: 1.5em;
    margin-bottom: 0.5em;
  }

  .collaborator-container {
    height: 17.5vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .input-container {
    margin-bottom: 1em;
  }

  .input-title {
    font-weight: bold;
  }

  .main-input-button {
    font-weight: bold;
    background-color: #212441;
    padding: 0.5em;
  }

  .main-input-button:hover {
    font-weight: bold;
    background-color: #FFFFFF;
    color: #212441;
    padding: 0.5em;
  }

  .secondary-input-button {
    background-color: white;
    color: #212441;
    padding: 0.5em;
  }

  .secondary-input-button:hover {
    background-color: #212441;
    color: white;
    padding: 0.5em;
  }

  .invalid-text {
    color: red;
    text-align: center;
  }
</style>
